import { reactive, computed } from 'vue'
import { port, domain, apiDomain, useDevAPI } from '../../../config/config/settings.json'
import { configureSessionApi } from '../helpers/sessionApi.js'

const isDev = process.env.NODE_ENV != 'production'
let api = useDevAPI ? `http://localhost:${port}` : apiDomain
if (!isDev) api = apiDomain
configureSessionApi(api)

const store = reactive({
	user: {},
	api,
	// cart: JSON.parse(window.sessionStorage.getItem('cart') || '[]'),
	cart: {
		cartItems: [],
		certificates: [],
		coupon: {
			code: '',
		},
		couponDiscount: 0,
		couponShipping: false,
		certificate: null,
		certificateDiscount: 0,
	},

	editingKit: null, //editingKitId ? await Mango.birthKit(editingKitId, { depthLimit: 1 }) : null
	birthKitOptional: null,
	cartInitialized: false,

	// Banner state
	showBanner: false, // Default to false, only show when an active banner is fetched
	bannerChecked: false, // True after banner fetch completes (whether banner exists or not)

	// Banner methods
	showBannerIfActive() {
		this.showBanner = true
		this.bannerChecked = true
	},

	hideBanner() {
		this.showBanner = false
		this.bannerChecked = true
	},
})

const checkoutState = reactive({
	loadingCheckout: false,
	showOverlay: false,
	overlayData: {
		message: 'Processing...',
		spinnerColor: 'border-t-white',
		spinnerSize: 'h-8 w-8',
	},
	applePayInstance: null,
	addresses: [],
	bcOrderId: null,
	braintreeClientToken: null,
	deviceData: null,
	orderDetails: {
		comments: '',
		isDigitalOnly: false,
	},
	guest: {
		email: '', // placeholder until guest email modal is implemented
		emailInput: '',
		firstName: '',
		lastName: '',
		subscribe: false,
	},
	payment: {
		storeCredit: null,
		useStoreCredit: false,
		selectedCard: null,
		newCard: {
			name: '',
			number: '',
			expiry: '',
			cvv: '',
		},
		saveNewCard: false,
		setNewCardAsDefault: false,
		addingNewCard: false,
		cardCVV: null,
		newACH: {
			accountType: 'checking',
			accountNumber: '',
			routingNumber: '',
			ownershipType: 'personal',
			firstName: '',
			lastName: '',
			businessName: '',
		},
		addingNewACH: false,
		selectedACH: null,
		taxes: [],
	},
	shipping: {
		addressInitialized: false,
		lastAddressHash: null,
		billingMatchesShipping: false,
		consignmentId: null,
		fetching: false,
		newAddress: {
			mangoId: null,
		},
		saveNewShippingAddress: false,
		saving: false,
		selectedShipping: null,
		shippingAddress: null,
		shippingOptions: [],
	},
	billing: {
		billingAddress: {},
		saveNewBillingAddress: false,
		saving: false,
		newAddress: {
			mangoId: null,
		},
	},
})

const checkout = reactive({
	...checkoutState,

	// computed properties as getters
	get loading() {
		return this.loadingCheckout
	},
	get couponType() {
		return store?.cart?.coupon?.type || ''
	},

	get couponAmount() {
		const value = store?.cart?.coupon?.amount || 0
		return Number(parseFloat(value).toFixed(2))
	},

	get couponDiscountSummary() {
		if (!this.couponAmount && this.couponType !== 'free_shipping') {
			return null
		}

		if (this.couponType === 'percentage_discount') {
			return `${parseFloat(this.couponAmount).toFixed(2)}% off each item`
		} else {
			let formatAmount = (amount) => {
				return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
			}

			if (this.couponType === 'per_item_discount') {
				let formattedAmount = formatAmount(this.couponAmount)
				return `${formattedAmount} off each item`
			} else if (this.couponType === 'per_total_discount') {
				let formattedAmount = formatAmount(this.couponAmount)
				return `${formattedAmount} off the order total`
			} else if (this.couponType === 'shipping_discount') {
				let formattedAmount = formatAmount(this.shippingDiscount)
				return `${formattedAmount} off the shipping total`
			} else if (this.couponType === 'free_shipping') {
				return `Free Shipping`
			}
		}
	},

	get shippingTotal() {
		const value = this.shipping.selectedShipping?.cost || 0
		return Number(parseFloat(value).toFixed(2))
	},

	get shippingDiscount() {
		let value
		if (this.couponType === 'free_shipping') {
			value = this.shippingTotal
		} else if (this.couponType === 'shipping_discount') {
			let discount = parseFloat(this.couponAmount)
			if (discount > this.shippingTotal) discount = this.shippingTotal
			value = discount
		} else {
			// shippingDiscount only to be used for shipping coupons.
			// Other types need to use the cart.couponDiscount returned from Mango
			value = 0
		}
		return Number(parseFloat(value).toFixed(2))
	},

	get shippingText() {
		if (this.shipping.selectedShipping?.id) {
			return this.shipping.selectedShipping?.cost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
		} else {
			return 'Add Info'
		}
	},

	get showShippingDisclaimer() {
		if (this.shipping.shippingAddress?.countryCode?.length && this.shipping.shippingAddress?.countryCode !== 'US') {
			return true
		} else {
			return false
		}
	},

	get isTexasShippingAddress() {
		if (!this.shipping.shippingAddress?.stateOrProvince) {
			return false
		}

		// If the shipping address has a countryCode that's not US, it's not Texas
		if (this.shipping.shippingAddress?.countryCode !== 'US') {
			return false
		}

		// Check if the state is Texas by looking at the stateOrProvince field
		if (this.shipping.shippingAddress?.stateOrProvince === 'Texas') {
			return true
		}

		return true
	},

	get couponDiscount() {
		// Applied non-shipping coupons are already factored into
		// the cart total returned from Big Commerce.
		// Only use for display purposes.

		if (this.couponType === 'free_shipping' || this.couponType === 'shipping_discount') {
			return this.shippingDiscount
		} else {
			const value = store?.cart?.couponDiscount || 0
			return Number(parseFloat(value).toFixed(2))
		}
	},

	get storeCredit() {
		const value = this.payment.useStoreCredit ? Math.min(store.user?.storeCreditAmounts[0]?.amount || 0, this.grandTotal) : 0
		return Number(parseFloat(value).toFixed(2))
	},

	get grandTotal() {
		// Total cart amount, including applicable discounts, BEFORE
		// processing payment.
		// Store Credit and Gift Certificate discounts are handled as payment forms,
		// so they are not factored into the grandTotal.
		const value = (store?.cart?.total || 0) + this.shippingTotal
		return Number(parseFloat(value).toFixed(2))
	},

	get giftCertificateAmount() {
		let value
		if (!store?.cart?.certificate?.balance) return 0
		if (store?.cart?.certificate?.balance <= this.grandTotal) {
			value = store.cart.certificate.balance
		} else if (store?.cart?.certificate?.balance > this.grandTotal) {
			value = this.grandTotal
		} else {
			value = 0
		}
		return Number(parseFloat(value).toFixed(2))
	},

	get displayTotal() {
		const value = this.grandTotal - this.storeCredit - this.giftCertificateAmount - this.shippingDiscount
		return parseFloat(parseFloat(value).toFixed(2))
	},

	get remainingBalance() {
		// This value is used as the "grand total" when processing via braintree gateway
		// Per processing order, any storeCredit or gift certificates will already have
		// been processed via the Big Commerce payment gateway
		const value = this.grandTotal - this.storeCredit - this.giftCertificateAmount - this.shippingDiscount
		return parseFloat(parseFloat(value).toFixed(2))
	},

	get paymentNotRequired() {
		return this.displayTotal <= 0
	},

	get isDigitalOnly() {
		return !store.cart.cartItems.length && !!store.cart.certificates.length
	},

	// checkout validation getters
	get emailValidated() {
		return !!store?.user?.email || !!this.guest?.email
	},

	get shippingAddressValidated() {
		const shippingAddress = this.shipping.shippingAddress || {}
		const missingFields = []

		if (!shippingAddress.firstName) missingFields.push('First Name')
		if (!shippingAddress.lastName) missingFields.push('Last Name')
		if (!shippingAddress.address1) missingFields.push('Address')
		if (!shippingAddress.city) missingFields.push('City')
		if (!shippingAddress.stateOrProvince) missingFields.push('State/Province')
		if (!shippingAddress.postalCode) missingFields.push('Postal Code')
		if (!shippingAddress.countryCode) missingFields.push('Country')
		if (!shippingAddress.phone) missingFields.push('Phone')

		if (missingFields.length > 0) {
			return {
				value: false,
				message: `Missing shipping information: ${missingFields.join(', ')}`,
			}
		}

		return { value: true }
	},

	get shippingMethodSelected() {
		return !!this.shipping.selectedShipping?.id
	},

	get billingAddressValidated() {
		const billingAddress = this.billing.billingAddress || {}
		const missingFields = []

		if (!billingAddress.firstName) missingFields.push('First Name')
		if (!billingAddress.lastName) missingFields.push('Last Name')
		if (!billingAddress.address1) missingFields.push('Address')
		if (!billingAddress.city) missingFields.push('City')
		if (!billingAddress.stateOrProvince) missingFields.push('State/Province')
		if (!billingAddress.postalCode) missingFields.push('Postal Code')
		if (!billingAddress.countryCode) missingFields.push('Country')

		if (missingFields.length > 0) {
			return {
				value: false,
				message: `Missing billing information: ${missingFields.join(', ')}`,
			}
		}

		return { value: true }
	},

	get expressCheckoutValidated() {
		if (!this.emailValidated) {
			return { value: false, message: 'Missing email address' }
		}

		if (!this.isDigitalOnly) {
			if (!this.shippingAddressValidated.value) {
				return { value: false, message: this.shippingAddressValidated.message }
			}

			if (!this.shippingMethodSelected) {
				return { value: false, message: 'No shipping method selected' }
			}
		}

		if (!this.billingAddressValidated.value) {
			return { value: false, message: this.billingAddressValidated.message }
		}

		return { value: true }
	},

	// User Methods
	updateGuestEmail(email) {
		this.guest.email = email
		window.sessionStorage.setItem('guest', JSON.stringify(this.guest))
	},

	// Shipping Methods
	resetShipping() {
		this.shipping.shippingOptions = []
		this.shipping.selectedShipping = null
		this.shipping.consignmentId = null
		this.shipping.lastAddressHash = null
		this.shipping.billingMatchesShipping = false
		window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
	},

	resetShippingAddress() {
		this.shipping.selectedShipping = null
		this.shipping.shippingOptions = []
		this.shipping.shippingAddress = null
		this.shipping.consignmentId = null
		this.shipping.lastAddressHash = null
		this.shipping.billingMatchesShipping = false
		window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
	},

	updateShippingOptions(options) {
		this.shipping.shippingOptions = options
	},

	selectShipping(shipping) {
		this.shipping.selectedShipping = shipping
		window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
	},

	setDefaultShippingAddress() {
		let defaultAddress = this.addresses.find((address) => address.isDefault === true)
		if (!defaultAddress && this.addresses.length > 0) {
			defaultAddress = this.addresses[this.addresses.length - 1]
		}
		this.shipping.shippingAddress = defaultAddress || null
		window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
	},

	setDefaultShippingMethod() {
		this.shipping.selectedShipping = this.shipping.shippingOptions[0]
		window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
	},

	setShippingAddress(address) {
		this.shipping.shippingAddress = address
		window.sessionStorage.setItem('shipping', JSON.stringify(this.shipping))
	},

	setShippingOptions(options) {
		this.shipping.shippingOptions = options
	},

	updateConsignmentId(id) {
		this.shipping.consignmentId = id
	},

	updateAddressInitialized(value) {
		this.shipping.addressInitialized = value
	},

	setLastAddressHash(hash) {
		this.shipping.lastAddressHash = hash
	},

	// Billing Methods
	setDefaultBillingAddress() {
		let defaultAddress = this.addresses.find((address) => address.isDefault === true)
		if (!defaultAddress && this.addresses.length > 0) {
			defaultAddress = this.addresses[this.addresses.length - 1]
		}
		this.billing.billingAddress = defaultAddress || null
		window.sessionStorage.setItem('billing', JSON.stringify(this.billing))
	},

	setBillingAddress(address) {
		this.billing.billingAddress = address
		window.sessionStorage.setItem('billing', JSON.stringify(this.billing))
	},

	updateBillingMatchesShipping(value) {
		this.shipping.billingMatchesShipping = value
	},

	copyShippingToBilling() {
		this.billing.billingAddress = JSON.parse(JSON.stringify(this.shipping.shippingAddress))
	},

	resetBillingAddress() {
		this.billing.billingAddress = null
		window.sessionStorage.removeItem('billing')
	},

	// Payment Methods
	selectCard(card) {
		this.payment.selectedCard = card
	},
	setNewCard(card) {
		this.payment.newCard = card
	},
	updateSaveNewCard(value) {
		this.payment.saveNewCard = value
	},
	updateSetNewCardAsDefault(value) {
		this.payment.setNewCardAsDefault = value
	},
	setCardCVV(value) {
		this.payment.cardCVV = value
	},
	updateAddingNewCard(value) {
		this.payment.addingNewCard = value
	},

	selectACH(account) {
		this.payment.selectedACH = account
	},
	setNewACH(account) {
		this.payment.newACH = account
	},
	updateAddingNewACH(value) {
		this.payment.addingNewACH = value
	},

	updateTaxes(taxes) {
		this.payment.taxes = taxes
	},

	// General
	updateLoading(value) {
		this.loadingCheckout = value
	},
	setAddresses(addresses) {
		this.addresses = addresses
		window.sessionStorage.setItem('addresses', JSON.stringify(this.addresses))
	},
	addressesPush(address) {
		this.addresses.push(address)
		window.sessionStorage.setItem('addresses', JSON.stringify(this.addresses))
	},
	initializeAddresses(addresses) {
		this.addresses = addresses
		window.sessionStorage.setItem('addresses', JSON.stringify(this.addresses))
		this.setDefaultShippingAddress()
		this.setDefaultBillingAddress()
	},
	clearAddresses() {
		this.addresses = []
		window.sessionStorage.removeItem('addresses')
	},
	setOrderComments(comments) {
		this.orderDetails.comments = comments
	},
	resetGuest() {
		this.guest = {
			email: '',
			emailInput: '',
			firstName: '',
			lastName: '',
			subscribe: false,
		}
		window.sessionStorage.setItem('guest', JSON.stringify(this.guest))
	},

	// Overlay
	updateShowOverlay(value) {
		this.showOverlay = value
	},
	updateOverlayData(data) {
		this.overlayData = data
	},
})

export function useStore() {
	return store
}

export function useCheckout() {
	return checkout
}
